/**
 * QR del portal de clientes al pie de los recibos de pago.
 *
 * Solo los negocios con el portal activo (Configuración > Portal de Clientes)
 * lo llevan: /api/portal_estado lo dice para cualquier usuario autenticado.
 * La consulta se hace al montar la pantalla de cobro, no al imprimir, para que
 * el recibo siga saliendo al instante; si falla, el recibo sale sin QR.
 *
 *   usePortalQr();                       // en la pantalla que imprime
 *   imprimirReciboPdf({ ..., qr: portalQrActual() });
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useMaterialUIController } from "context";
import { apiPost } from "lib/api";

// El portal lo sirve el backend (Flask), no el dev server del dashboard.
const ORIGEN_PORTAL = process.env.REACT_APP_SERVIDOR || window.location.origin;
export const MENSAJE_QR = "Escanea para ver tu cuenta";
// El estado se vuelve a pedir pasado este tiempo: quien activa el portal ve el
// QR en los recibos sin tener que cerrar la sesión.
const VIGENCIA_MS = 15 * 60 * 1000;

// Un solo estado por usuario logueado; cambiar de usuario lo descarta.
let cache = { usuario: null, leido: 0, qr: null, pendiente: null };

// `o=<origen>` deja en la analítica del portal de dónde vino la visita
// (recibo, morosidad...).
// `ruta` es lo que devuelve el backend: "/portal" (relativa a este servidor) o
// la URL completa del dominio propio del portal (PORTAL_HOST).
export const basePortal = (ruta = "/portal") =>
  (/^https?:\/\//i.test(ruta) ? ruta : `${ORIGEN_PORTAL.replace(/\/+$/, "")}${ruta}`).replace(
    /\/+$/,
    ""
  );
export const urlPortal = (origen, ruta) => `${basePortal(ruta)}/?o=${origen}`;
export const urlPortalRecibo = (ruta) => urlPortal("recibo", ruta);

export async function cargarPortalQr(usuario, password) {
  if (!usuario || !password) return null;
  const fresco = cache.usuario === usuario && Date.now() - cache.leido < VIGENCIA_MS;
  if (fresco) return cache.pendiente || cache.qr;
  const pendiente = (async () => {
    try {
      const { ok, data } = await apiPost("/api/portal_estado", { usuario, password });
      if (!ok || !data.habilitado) return null;
      const url = urlPortalRecibo(data.url);
      const dataUrl = await QRCode.toDataURL(url, {
        margin: 0,
        width: 300,
        errorCorrectionLevel: "M",
      });
      return { dataUrl, url, ruta: data.url, mensaje: MENSAJE_QR };
    } catch (e) {
      return null;
    }
  })();
  cache = { usuario, leido: Date.now(), qr: null, pendiente };
  const qr = await pendiente;
  if (cache.pendiente === pendiente) cache = { ...cache, qr, pendiente: null };
  return qr;
}

// QR listo para el recibo, o null (portal apagado, aún cargando o sin red).
export const portalQrActual = () => cache.qr;

// Deja el QR cargado mientras la pantalla está abierta. Devuelve el estado
// ({dataUrl, url, ruta, mensaje} o null) para las pantallas que lo pintan.
export function usePortalQr() {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;
  const [qr, setQr] = useState(cache.usuario === usuarioLog ? cache.qr : null);
  useEffect(() => {
    let activo = true;
    cargarPortalQr(usuarioLog, claveLog).then((r) => {
      if (activo) setQr(r);
    });
    return () => {
      activo = false;
    };
  }, [usuarioLog, claveLog]);
  return qr;
}
